import * as Yup from 'yup'
import { filter, get, isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { isNonEmptyNumber } from '@labrav/common'
import { RecurrentPriceModeType } from '../../types/flyout'
import { emptyFieldMessage } from './messagesContants'
import './globalValidations'
import { markupValidationMsg } from '../../components/AzureCombinedForm/constants'
declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      timeZone: string,
      message?: string
    ): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath)

      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

Yup.addMethod(
  Yup.array,
  'installmentsValidation',
  function (propertyPath, timeZone, message) {
    return this.test('installmentsValidation', '', function (list: any) {
      const errors: any = []
      const acceptByDate = this.options?.context?.draftOffer?.acceptBy
      const paymentDatesBeforeAcceptBy = filter(
        list,
        (item: any) =>
          item[propertyPath] &&
          DateTime.fromISO(item[propertyPath], { zone: timeZone }) <
            DateTime.fromISO(acceptByDate, { zone: timeZone })
      ).map(item => item[propertyPath])
      list?.forEach((item: any, index: number) => {
        if (
          paymentDatesBeforeAcceptBy.length > 1 &&
          paymentDatesBeforeAcceptBy.includes(item[propertyPath])
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

export const azureCombinedFormValidationSchema = (): Yup.AnyObjectSchema =>
  Yup.object().shape({
    labraResellerId: Yup.string()
      .nullable()
      .required('Select a reseller account.'),
    draftOffer: Yup.object().shape({
      companyName: Yup.string().required(emptyFieldMessage),
      mppoName: Yup.string().required(emptyFieldMessage),
      preparedBy: Yup.string()
        .email('Enter a valid email address.')
        .required('Enter a valid email address.'),
      mppoDescription: Yup.string().nullable(),
      beneficiaryId: Yup.string().required(emptyFieldMessage),
      acceptBy: Yup.string()
        .nullable()
        .required('Provide the expiration date.'),
      labraProductId: Yup.string().required('Select a product.'),
      existingContractRenewal: Yup.mixed().test(
        'is-renewal-required',
        'Select Yes or No.',
        function (value: string | boolean) {
          if (value === undefined) {
            return false
          }
          return true
        }
      ),
      variableStartDate: Yup.boolean().nullable(),
      offerStartDate: Yup.string().when('variableStartDate', {
        is: false,
        then: Yup.string()
          .nullable()
          .required('Provide the purchase start month.')
          .test(
            'check-specified-date-for-expiration',
            'Start month cannot be before expiration date.',
            function (value?: string | null) {
              const acceptByDate = get(
                this.options?.context,
                'draftOffer.acceptBy'
              )
              if (acceptByDate) {
                return (
                  DateTime.fromISO(acceptByDate) <=
                  DateTime.fromISO(value || '')
                )
              }
              return true
            }
          ),
        otherwise: Yup.string().nullable(),
      }),
      offerExpirationDate: Yup.string()
        .nullable()
        .required('Provide the purchase end month.')
        .test(
          'check-specified-date',
          'End month cannot be before start month.',
          function (value?: string | null) {
            const offerStartDate = get(
              this.options?.context,
              'draftOffer.offerStartDate'
            )
            const variableStartDate = get(
              this.options?.context,
              'draftOffer.variableStartDate'
            )
            if (offerStartDate && !variableStartDate) {
              return (
                DateTime.fromISO(offerStartDate) <=
                DateTime.fromISO(value || '')
              )
            }
            return true
          }
        )
        .test(
          'check-specified-date-for-expiration',
          'End month cannot be before expiration date.',
          function (value?: string | null) {
            const acceptByDate = get(
              this.options?.context,
              'draftOffer.acceptBy'
            )
            if (acceptByDate) {
              return (
                DateTime.fromISO(acceptByDate) <= DateTime.fromISO(value || '')
              )
            }
            return true
          }
        ),
      plans: Yup.array()
        .of(
          Yup.object().shape({
            labraPlanId: Yup.string().required('Select a plan.'),
            displayName: Yup.string().required(emptyFieldMessage),
            description: Yup.string().nullable(),
            buyerPrice: Yup.number()
              .nullable()
              .typeError('Enter a valid buyer price.')
              .test(
                'buyer-price-validation',
                'Enter value (max 5 decimals, ≤100,000,000).',
                function (value) {
                  if (value === null || value === undefined) {
                    return false
                  }

                  if (value < 0) {
                    return this.createError({
                      message: 'Buyer price must be a positive value.',
                    })
                  }

                  if (value > 100000000) {
                    return false
                  }

                  const decimalPlaces = (value.toString().split('.')[1] || '')
                    .length
                  if (decimalPlaces > 5) {
                    return false
                  }

                  return true
                }
              )
              .test(
                'buyer-price-tcv-validation',
                'Buyer price cannot be less than or equal to the discounted price.',
                function (value) {
                  if (value === null || value === undefined) {
                    return true
                  }

                  // Get the tcvValue from the same plan
                  const pricePerPayment =
                    this.parent?.pricing?.recurrentPrice?.prices?.[0]
                      ?.pricePerPaymentInUsd
                  const installmentsTcv =
                    this.parent?.pricing?.recurrentPrice?.prices?.[0]
                      ?.installments?.tcv

                  // tcvValue is either pricePerPayment or installmentsTcv depending on payment type
                  const tcvValue =
                    installmentsTcv !== null && installmentsTcv !== undefined
                      ? installmentsTcv
                      : pricePerPayment

                  if (
                    tcvValue !== null &&
                    tcvValue !== undefined &&
                    value <= tcvValue
                  ) {
                    return false
                  }

                  return true
                }
              )
              .test(
                'markup-percentage-validation',
                markupValidationMsg,
                function (value) {
                  if (value === null || value === undefined) {
                    return true
                  }
                  const markupPercentage = this.parent?.markupPercentage

                  if (
                    markupPercentage === null ||
                    markupPercentage === undefined
                  ) {
                    return true
                  }
                  if (markupPercentage > 100) {
                    return false
                  }

                  return true
                }
              ),
            pricing: Yup.object().shape({
              recurrentPrice: Yup.object().shape({
                recurrentPriceMode: Yup.string(),
                prices: Yup.array().of(
                  Yup.object().shape({
                    pricePerPaymentInUsd: Yup.number()
                      .nullable()
                      .test(
                        'mutual-exclusivity',
                        'Enter value (max 5 decimals, ≤100,000,000).',
                        function (value) {
                          const installments =
                            this.parent?.installments?.schedule

                          if ((installments || [])?.length) {
                            return true
                          }
                          if (
                            !installments &&
                            (value === null || value === undefined)
                          ) {
                            return false
                          }

                          if (
                            !installments &&
                            value !== null &&
                            value !== undefined
                          ) {
                            if (value > 100000000) {
                              return false
                            }
                            const decimalPlaces = (
                              value.toString().split('.')[1] || ''
                            ).length
                            if (decimalPlaces > 5) {
                              return false
                            }
                          }

                          return true
                        }
                      ),
                    billingTerm: Yup.object().shape({
                      value: Yup.number()
                        .typeError('Select a contract duration.')
                        .required('Select a contract duration.'),
                    }),
                    installments: Yup.object().when('pricePerPaymentInUsd', {
                      is: (value: number) =>
                        value === null || value === undefined,
                      then: Yup.object().shape({
                        tcv: Yup.number()
                          .nullable()
                          .test(
                            'mutual-exclusivity',
                            'Enter value (max 5 decimals, ≤100,000,000).',
                            function (value) {
                              if (value === null || value === undefined) {
                                return false
                              }

                              if (value !== null && value !== undefined) {
                                if (value > 100000000) {
                                  return false
                                }
                                const decimalPlaces = (
                                  value.toString().split('.')[1] || ''
                                ).length
                                if (decimalPlaces > 5) {
                                  return false
                                }
                              }

                              return true
                            }
                          ),
                        schedule: Yup.array()
                          .of(
                            Yup.object().shape({
                              pricePerPaymentInUsd: Yup.number()
                                .typeError(
                                  'Enter value (max 5 decimals, ≤100,000,000).'
                                )
                                .required(
                                  'Enter value (max 5 decimals, ≤100,000,000).'
                                )
                                .max(
                                  100000000,
                                  'Enter value (max 5 decimals, ≤100,000,000).'
                                )
                                .checkFiveDecimalpointValidation({
                                  message:
                                    'Enter value (max 5 decimals, ≤100,000,000).',
                                }),
                              chargeDate: Yup.string()
                                .nullable()
                                .when('isImmediateInstallment', {
                                  is: true,
                                  then: Yup.string().nullable().notRequired(),
                                  otherwise: Yup.string()
                                    .nullable()
                                    .required('Invoice date is required')
                                    .test(
                                      'check-all-dates-after-start-date',
                                      'Invoice date cannot be before the start date.',
                                      function (value?: string | null) {
                                        const offerStartDate = get(
                                          this.options?.context,
                                          'draftOffer.offerStartDate'
                                        )
                                        const variableStartDate = get(
                                          this.options?.context,
                                          'draftOffer.variableStartDate'
                                        )

                                        if (variableStartDate) return true

                                        if (!offerStartDate) return true

                                        if (value && offerStartDate) {
                                          return (
                                            DateTime.fromISO(value) >=
                                            DateTime.fromISO(offerStartDate)
                                          )
                                        }
                                        return true
                                      }
                                    )
                                    .test(
                                      'check-charge-date-within-billing-term',
                                      'Invoice date cannot be after the contract duration.',
                                      function (value?: string | null) {
                                        if (!value) return true

                                        const currentPlanPath =
                                          this.path?.split('.installments')[0]
                                        const selectedPrice = get(
                                          this.options?.context,
                                          currentPlanPath || ''
                                        )
                                        const billingTerm =
                                          selectedPrice?.billingTerm

                                        if (
                                          !billingTerm ||
                                          !billingTerm.type ||
                                          !billingTerm.value
                                        ) {
                                          return true
                                        }

                                        const offerStartDate = get(
                                          this.options?.context,
                                          'draftOffer.offerStartDate'
                                        )
                                        const variableStartDate = get(
                                          this.options?.context,
                                          'draftOffer.variableStartDate'
                                        )

                                        const startDate =
                                          offerStartDate && !variableStartDate
                                            ? DateTime.fromISO(offerStartDate)
                                            : DateTime.now()

                                        let contractEndDate: DateTime
                                        if (billingTerm.type === 'year') {
                                          contractEndDate = startDate.plus({
                                            years: billingTerm.value,
                                          })
                                        } else if (
                                          billingTerm.type === 'month'
                                        ) {
                                          contractEndDate = startDate.plus({
                                            months: billingTerm.value,
                                          })
                                        } else {
                                          return true
                                        }

                                        contractEndDate = contractEndDate
                                          .minus({ days: 1 })
                                          .endOf('day')

                                        const chargeDateTime =
                                          DateTime.fromISO(value)
                                        return chargeDateTime <= contractEndDate
                                      }
                                    ),
                                }),
                            })
                          )
                          .test(
                            'unique-charge-dates',
                            'Duplicate invoice dates are not allowed.',
                            function (list: any) {
                              if (!list || list.length === 0) return true

                              const errors: any = []
                              const validDates = list
                                .filter(
                                  (item: any) =>
                                    item.chargeDate &&
                                    !item.isImmediateInstallment
                                )
                                .map((item: any) => item.chargeDate)

                              const duplicateDates = validDates.filter(
                                (date: string, index: number) =>
                                  validDates.indexOf(date) !== index
                              )

                              if (duplicateDates.length > 0) {
                                list.forEach((item: any, index: number) => {
                                  if (
                                    duplicateDates.includes(item.chargeDate)
                                  ) {
                                    errors.push(
                                      this.createError({
                                        path: `${this.path}[${index}].chargeDate`,
                                        message:
                                          'Duplicate invoice dates are not allowed.',
                                      })
                                    )
                                  }
                                })
                              }

                              if (!isEmpty(errors)) {
                                throw new Yup.ValidationError(errors)
                              }

                              return true
                            }
                          )
                          .installmentsValidation(
                            'chargeDate',
                            'UTC',
                            'Only one invoice date can be before the offer expiration date.'
                          ),
                      }),
                      otherwise: Yup.object().nullable(),
                    }),
                  })
                ),
                userLimits: Yup.object().when('recurrentPriceMode', {
                  is: RecurrentPriceModeType.PERUSER,
                  then: Yup.object().shape({
                    min: Yup.number()
                      .typeError(emptyFieldMessage)
                      .required(emptyFieldMessage),
                    max: Yup.number()
                      .typeError(emptyFieldMessage)
                      .required(emptyFieldMessage)
                      .test(
                        'min-max',
                        'Enter a value above min user count.',
                        function (value, context) {
                          if (
                            isNonEmptyNumber(value) &&
                            context?.parent?.min > value!
                          ) {
                            return false
                          }
                          return true
                        }
                      ),
                  }),
                  otherwise: Yup.object().nullable(),
                }),
              }),
              customMeters: Yup.object().shape({
                meters: Yup.lazy(value => {
                  if (!isEmpty(value)) {
                    const validationObject = Yup.object().shape({
                      includedQuantities: Yup.array().of(
                        Yup.object().shape({
                          isInfinite: Yup.boolean(),
                          quantity: Yup.number()
                            .nullable()
                            .when('isInfinite', {
                              is: false,
                              then: Yup.number()
                                .typeError('Enter base quantity.')
                                .required('Enter base quantity.'),
                            }),
                        })
                      ),
                      pricePerPaymentInUsd: Yup.number()
                        .nullable()
                        .when('includedQuantities', {
                          is: (includedQuantities: any[]) =>
                            !includedQuantities?.[0]?.isInfinite,
                          then: Yup.number()
                            .typeError(
                              'Enter value (max 5 decimals, ≤100,000,000).'
                            )
                            .required(
                              'Enter value (max 5 decimals, ≤100,000,000).'
                            )
                            .max(
                              100000000,
                              'Enter value (max 5 decimals, ≤100,000,000).'
                            )
                            .checkFiveDecimalpointValidation({
                              message:
                                'Enter value (max 5 decimals, ≤100,000,000).',
                            }),
                        }),
                    })
                    const newEntries = Object.keys(value).reduce(
                      (acc, val) => ({
                        ...acc,
                        [val]: validationObject,
                      }),
                      {}
                    )

                    return Yup.object().shape(newEntries)
                  }
                  return Yup.mixed().notRequired()
                }),
              }),
            }),
          })
        )
        .min(1, 'At least one plan is required.'),
    }),
  })

export default azureCombinedFormValidationSchema
